import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { fetchWithAuth } from '../utils/api';
import { tryMultipleEndpoints } from '../utils/api-helpers';
import toast from "react-hot-toast";
import { Check, ArrowLeft, Bell, Users, Send, AlertCircle } from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { Badge } from "src/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/components/ui/dialog";
import { Checkbox } from "src/components/ui/checkbox";
import { UserAvatar } from '../components/UserAvatar';
import { TableContainer } from '../components/ui/table-container';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_adjudicator: boolean;
}

// Update the Notification interface definition
interface Notification {
  id: number;
  recipients: number[];
  recipient_usernames?: string[]; // Add this field
  type: string;
  message: string;
  link: string;
  created_at: string;
  sent_by: {
    id: number;
    username: string;
  };
  sent_to_all: boolean;
}

const NotificationManagement: React.FC = () => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [sentNotifications, setSentNotifications] = useState<Notification[]>([]);
  const [showNewNotificationDialog, setShowNewNotificationDialog] = useState(false);
  const [showUserSelectionDialog, setShowUserSelectionDialog] = useState(false);
  
  // New notification form state
  const [notificationType, setNotificationType] = useState<string>("SYSTEM");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationLink, setNotificationLink] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  };

  // Enhanced fetchUsers function with multiple endpoint support
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      // Expanded list of endpoints to try
      const userEndpoints = [
        '/api/admin/users/',
        '/admin/users/',
        '/api/users/admin/',
        '/admin/user-list/',
        '/api/users/',
        '/users/'
      ];
      
      // Use tryMultipleEndpoints for robust fetching
      const response = await tryMultipleEndpoints(userEndpoints);
      
      if (response) {
        let userData: any[] = [];
        
        // Handle different response formats
        if (Array.isArray(response)) {
          userData = response;
        } else if (response.users && Array.isArray(response.users)) {
          userData = response.users;
        } else if (response.results && Array.isArray(response.results)) {
          userData = response.results;
        } else {
          // Try to find an array in the response object
          for (const key in response) {
            if (Array.isArray(response[key]) && response[key].length > 0) {
              if (response[key][0].username || response[key][0].email) {
                userData = response[key];
                break;
              }
            }
          }
        }
        
        if (userData && userData.length > 0) {
          // Process users to ensure consistent ID format
          const processedUsers = userData.map((user: any) => {
            // Ensure ID is a number for frontend use
            let userId = user.id;
            
            if (typeof userId === 'string') {
              // Handle UUIDs
              if (userId.includes('-')) {
                // For UUID/GUID format, just keep as is
              } else {
                // For numeric strings, convert to number
                userId = parseInt(userId, 10);
              }
            }
            
            // Fallback if ID is missing
            if (userId === null || userId === undefined) {
              userId = Math.floor(Math.random() * 10000);
              console.warn(`Missing ID for user ${user.username}, using generated ID: ${userId}`);
            }
            
            return {
              id: userId,
              username: user.username || 'Unknown',
              email: user.email || '',
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              is_staff: !!user.is_staff,
              is_adjudicator: !!user.is_adjudicator
            };
          });
          
          setUsers(processedUsers);
          console.log("Processed users data:", processedUsers);
        } else {
          // Use fallback mock data
          console.warn("No user data found in response, using fallback");
          setUsers(getMockUsers());
        }
      } else {
        console.error("Could not fetch users from any endpoint");
        setUsers(getMockUsers());
      }
    } catch (err: any) {
      console.error("Error in fetchUsers:", err);
      setUsers(getMockUsers());
    } finally {
      setLoading(false);
    }
  }, []);

  // Mock data function for fallback
  const getMockUsers = (): User[] => {
    const currentUsername = localStorage.getItem('username') || 'admin';
    return [
      {
        id: 1,
        username: currentUsername,
        email: `${currentUsername}@example.com`,
        first_name: 'Current',
        last_name: 'User',
        is_staff: true,
        is_adjudicator: false
      },
      {
        id: 2,
        username: 'user1',
        email: 'user1@example.com',
        first_name: 'Test',
        last_name: 'User',
        is_staff: false,
        is_adjudicator: true
      }
    ];
  };

  // Enhanced notification fetching with multiple endpoints
  const fetchSentNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const endpoints = [
        '/api/admin/notifications/manage/',
        '/api/notifications/manage/',
        '/api/admin/notifications/',
        '/admin/notifications/',
        '/api/notifications/admin/',
        '/notifications/admin/',
        '/api/notifications/sent/'
      ];
      
      let response = null;
      let lastError = null;
      
      // Try each endpoint until one works
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to fetch sent notifications from: ${endpoint}`);
          response = await fetchWithAuth(endpoint);
          
          if (response) {
            console.log(`Successfully got notifications from ${endpoint}:`, response);
            break;
          }
        } catch (err) {
          console.error(`Failed to fetch from ${endpoint}:`, err);
          lastError = err;
        }
      }
      
      if (response) {
        // Format notifications for display
        if (Array.isArray(response)) {
          console.log("Processing notification data:", response);
          
          // Format the notifications to match our interface
          const formattedNotifications = response.map(n => {
            // Process recipients
            let recipients = [];
            if (Array.isArray(n.recipients)) {
              recipients = n.recipients;
            } else if (typeof n.recipients === 'string') {
              try {
                recipients = JSON.parse(n.recipients);
              } catch {
                recipients = n.recipients ? [n.recipients] : [];
              }
            }
            
            // Process recipient usernames - FIX: use n.recipient_usernames, not n.recipients
            let recipient_usernames = [];
            if (Array.isArray(n.recipient_usernames)) {
              recipient_usernames = n.recipient_usernames;
            } else if (typeof n.recipient_usernames === 'string') {
              try {
                recipient_usernames = JSON.parse(n.recipient_usernames);
              } catch {
                recipient_usernames = [];
              }
            }
            
            // If we have recipients but no usernames, try to look them up
            if (recipients.length > 0 && recipient_usernames.length === 0) {
              // Use a functional approach that doesn't depend on the users state
              // This breaks the dependency cycle
              recipient_usernames = recipients.map((id: number) => `User #${id}`);
            }
            
            return {
              id: n.id || 0,
              recipients: recipients,
              recipient_usernames: recipient_usernames,
              type: n.type || 'SYSTEM',
              message: n.message || '',
              link: n.link || '',
              created_at: n.created_at || new Date().toISOString(),
              sent_by: n.sent_by || {
                id: 0,
                username: 'System'
              },
              sent_to_all: !!n.sent_to_all
            };
          });
          
          setSentNotifications(formattedNotifications);
        } else {
          console.warn("Response is not an array:", response);
          setSentNotifications([]);
        }
      } else {
        if (lastError) {
          console.error("Could not fetch notifications from any endpoint:", lastError);
        } else {
          console.error("No valid response received from any endpoint");
        }
        setSentNotifications([]);
      }
    } catch (err: unknown) {
      console.error("Error in fetchSentNotifications:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch sent notifications";
      setError(errorMessage);
      setSentNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []); // Remove users from dependency array

  const handleBack = () => {
    pageTransition(() => {
      navigate("/dashboard");
    });
  };

const handleSendNotification = async () => {
  if (!notificationMessage.trim()) {
    toast.error("Please enter a notification message");
    return;
  }

  if (!sendToAll && selectedUserIds.length === 0) {
    toast.error("Please select at least one recipient");
    return;
  }
  
  try {
    setLoading(true);
    setError("");
    
    // Get current user information
    const userId = localStorage.getItem("userId") || localStorage.getItem("user_id") || "0";
    
    // Get selected usernames for display
    const selectedRecipientUsernames = selectedUserIds.map(id => {
      const user = users.find(user => user.id === id);
      return user?.username || `User #${id}`;
    });
    
    // Format recipient IDs as UUIDs for the backend
    const payload = {
      type: notificationType,
      message: notificationMessage,
      link: notificationLink.trim() || null,
      send_to_all: sendToAll,
      recipients: sendToAll ? [] : selectedUserIds.map(id => {
        const paddedId = String(id).padStart(12, '0');
        return `00000000-0000-0000-0000-${paddedId}`;
      }),
      sender_id: `00000000-0000-0000-0000-${String(userId).padStart(12, '0')}`
    };
    
    console.log("Sending notification with payload:", payload);
    
    // Use DRF endpoint
    const response = await fetchWithAuth('/api/admin/notifications/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    if (response) {
      // Format the response into our notification interface
      const responseData: Notification = {
        id: response.id || Date.now(),
        recipients: response.recipients || selectedUserIds,
        recipient_usernames: response.recipient_usernames || selectedRecipientUsernames,
        type: response.type || notificationType,
        message: response.message || notificationMessage,
        link: response.link || notificationLink,
        created_at: response.created_at || new Date().toISOString(),
        sent_by: {
          id: parseInt(userId),
          username: localStorage.getItem("username") || "current user"
        },
        sent_to_all: sendToAll
      };
      
      // Reset form
      setNotificationType("SYSTEM");
      setNotificationMessage("");
      setNotificationLink("");
      setSelectedUserIds([]);
      setSendToAll(false);
      
      // Close dialog
      setShowNewNotificationDialog(false);
      
      // Add the new notification to the list
      setSentNotifications(prev => [responseData, ...prev]);
      
      toast.success("Notification sent successfully");
      
      // Try to refresh notifications list
      setTimeout(() => {
        fetchSentNotifications().catch(err => 
          console.error("Failed to refresh notifications list after sending", err)
        );
      }, 1000);
    }
  } catch (err: any) {
    setError(err.message || "Failed to send notification");
    toast.error(err.message || "Failed to send notification");
    console.error("Error sending notification:", err);
  } finally {
    setLoading(false);
  }
};

  const handleUserSelection = (userId: number) => {
    console.log("Toggling selection for user ID:", userId, typeof userId);
    
    // Ensure userId is a number
    const numericId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
    
    // Verify the user exists before allowing selection
    const userExists = users.some(u => u.id === numericId);
    if (!userExists) {
      console.warn(`User with ID ${numericId} not found in users list`);
      return;
    }
    
    setSelectedUserIds(prev => {
      if (prev.includes(numericId)) {
        return prev.filter(id => id !== numericId);
      } else {
        return [...prev, numericId];
      }
    });
  };

  const handleSelectAllUsers = () => {
    const filteredUserIds = filterUsers().map(user => {
      // Ensure ID is a number
      const numericId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
      return numericId;
    });
    
    console.log("Available user IDs for selection:", filteredUserIds);
    
    if (selectedUserIds.length === filteredUserIds.length) {
      // If all are selected, deselect all
      setSelectedUserIds([]);
    } else {
      // Otherwise select all
      setSelectedUserIds(filteredUserIds);
    }
  };

  const filterUsers = () => {
    if (!Array.isArray(users)) {
      console.warn("Users is not an array:", users);
      return [];
    }
    
    return users.filter(user => 
      user.username.toLowerCase().includes(userFilter.toLowerCase()) ||
      user.email.toLowerCase().includes(userFilter.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(userFilter.toLowerCase())
    );
  };

  const getUsernamesText = () => {
    if (sendToAll) return "All users";
    
    if (selectedUserIds.length === 0) return "No users selected";
    
    // Create a mapping function that handles missing users
    const getUsernameById = (id: number) => {
      const user = users.find(user => user.id === id);
      return user ? user.username : `User #${id}`;
    };
    
    // Get selected usernames
    const selectedUsernames = selectedUserIds.map(getUsernameById);
    
    if (selectedUserIds.length > 3) {
      const firstThree = selectedUsernames.slice(0, 3).join(", ");
      return `${firstThree} and ${selectedUserIds.length - 3} more`;
    }
    
    return selectedUsernames.join(", ");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    // Initial data loading
    fetchUsers();
    fetchSentNotifications();
    
    // Set up a refresh interval for notifications
    const notificationRefreshInterval = setInterval(() => {
      fetchSentNotifications();
    }, 60000); // Refresh every minute
    
    // Clean up interval on component unmount
    return () => {
      clearInterval(notificationRefreshInterval);
    };
  }, [fetchUsers, fetchSentNotifications]);

  // Create a memoized lookup function for finding usernames
const findUsername = useCallback((id: number) => {
  const user = users.find(u => u.id === id);
  return user ? user.username : `User #${id}`;
}, [users]);

  // Update renderRecipientsCell to use the memoized lookup function
const renderRecipientsCell = useCallback((notification: Notification) => {
  if (notification.sent_to_all) {
    return <span className="text-sm dark:text-white light:text-gray-800">All Users</span>;
  }
  
  // Check if we have recipient usernames from the API
  if (notification.recipient_usernames && notification.recipient_usernames.length > 0) {
    const displayText = notification.recipient_usernames.length > 3
      ? `${notification.recipient_usernames.slice(0, 3).join(', ')} and ${notification.recipient_usernames.length - 3} more`
      : notification.recipient_usernames.join(', ');
    
    return (
      <span className="text-sm dark:text-white light:text-gray-800">
        {notification.recipient_usernames.length} user{notification.recipient_usernames.length !== 1 ? 's' : ''}: {displayText}
      </span>
    );
  }
  
  // If no usernames but we have recipient IDs, look up usernames
  if (notification.recipients && notification.recipients.length > 0) {
    const usernames = notification.recipients.map(findUsername);
    
    const displayText = usernames.length > 3
      ? `${usernames.slice(0, 3).join(', ')} and ${usernames.length - 3} more`
      : usernames.join(', ');
    
    return (
      <span className="text-sm dark:text-white light:text-gray-800">
        {notification.recipients.length} user{notification.recipients.length !== 1 ? 's' : ''}: {displayText}
      </span>
    );
  }
  
  return <span className="text-sm dark:text-white light:text-gray-800">No recipients</span>;
}, [findUsername]); // Now depends on the stable findUsername function instead of users directly

  return (
    <motion.div 
      className="p-4"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <Card className="w-full max-w-5xl mx-auto transition-colors duration-200 bg-gray-800 dark:bg-gray-800 light:bg-white shadow-lg rounded-lg">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="mr-2"
            >
              <ArrowLeft size={16} />
            </Button>
            <div>
              <CardTitle className="text-2xl font-bold dark:text-white light:text-gray-800">
                Notification Management
              </CardTitle>
              <p className="text-gray-400 text-sm">
                Create and manage system notifications
              </p>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowNewNotificationDialog(true)}
            variant="action"
          >
            <Bell size={16} className="mr-2" />
            <span>Send New Notification</span>
          </Button>
        </CardHeader>
        
        <CardContent className="p-6">
          {error && (
            <motion.div 
              variants={itemVariants}
              className="mb-6 p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg text-red-500"
            >
              <div className="flex items-center">
                <AlertCircle className="mr-2" size={20} />
                {error}
              </div>
            </motion.div>
          )}
          
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold mb-4 dark:text-white light:text-gray-800">
              Recently Sent Notifications
            </h2>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 dark:text-white light:text-gray-800">Loading notifications...</p>
              </div>
            ) : sentNotifications.length > 0 ? (
              <TableContainer>
                <table className="min-w-full divide-y dark:divide-gray-700 light:divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Message</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Recipients</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Sent At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 light:text-gray-500 uppercase tracking-wider">Sent By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700 light:divide-gray-200">
                    {sentNotifications.map(notification => (
                      <tr key={notification.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={`
                            ${notification.type === 'SYSTEM' ? 'bg-blue-500' : 
                              notification.type === 'ROUND_START' ? 'bg-green-500' : 
                              notification.type === 'RESULTS_AVAILABLE' ? 'bg-purple-500' : 
                              'bg-gray-500'} 
                            text-white`
                          }>
                            {notification.type.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <p className="dark:text-white light:text-gray-800 line-clamp-2">{notification.message}</p>
                          {notification.link && (
                            <span className="text-xs text-blue-500">
                              Has link: {notification.link}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {renderRecipientsCell(notification)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm dark:text-white light:text-gray-800">
                          {formatDate(notification.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm dark:text-white light:text-gray-800">
                          {notification.sent_by.username}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableContainer>
            ) : (
              <div className="text-center py-8">
                <Bell className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 dark:text-white light:text-gray-800">No notifications have been sent yet</p>
              </div>
            )}
          </motion.div>
        </CardContent>
      </Card>

      {/* New Notification Dialog */}
      <Dialog open={showNewNotificationDialog} onOpenChange={setShowNewNotificationDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Send New Notification</DialogTitle>
            <DialogDescription>
              Create a notification to send to selected users or everyone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-white light:text-gray-800">
                Notification Type
              </label>
              <Select
                value={notificationType}
                onValueChange={setNotificationType}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SYSTEM">System Notification</SelectItem>
                  <SelectItem value="ROUND_START">Round Starting</SelectItem>
                  <SelectItem value="RESULTS_AVAILABLE">Results Available</SelectItem>
                  <SelectItem value="ROLE_ASSIGNED">Role Assignment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-white light:text-gray-800">
                Message
              </label>
              <Textarea
                placeholder="Enter notification message..."
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-white light:text-gray-800">
                Link (Optional)
              </label>
              <Input
                placeholder="e.g., /round/123 or /dashboard"
                value={notificationLink}
                onChange={(e) => setNotificationLink(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Enter a relative path that users will be directed to when clicking the notification.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium dark:text-white light:text-gray-800 flex items-center space-x-2">
                <span>Recipients</span>
                <div className="flex-1"></div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sendToAll"
                    checked={sendToAll}
                    onCheckedChange={(checked: boolean | "indeterminate") => setSendToAll(checked === true)}
                    className="mr-1"
                  />
                  <label htmlFor="sendToAll" className="text-sm cursor-pointer">
                    Send to all users
                  </label>
                </div>
              </label>
              
              {!sendToAll && (
                <div className="border rounded-md p-2 flex justify-between items-center">
                  <div className="truncate">
                    {getUsernamesText()}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUserSelectionDialog(true)}
                  >
                    <Users size={16} className="mr-1" />
                    Select Users
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewNotificationDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSendNotification}
              disabled={loading}
            >
              <Send size={16} className="mr-2" />
              Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Selection Dialog */}
      <Dialog open={showUserSelectionDialog} onOpenChange={setShowUserSelectionDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Recipients</DialogTitle>
            <DialogDescription>
              Choose which users will receive this notification.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <div className="relative flex-1 mr-4">
                <Input
                  placeholder="Search users..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="pl-9"
                />
                <Users className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAllUsers}
              >
                {selectedUserIds.length === filterUsers().length ? "Deselect All" : "Select All"}
              </Button>
            </div>
            
            <div className="max-h-[400px] overflow-y-auto border rounded-md divide-y dark:divide-gray-700 light:divide-gray-200">
              {filterUsers().length > 0 ? (
                filterUsers().map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-700 hover:bg-opacity-30 cursor-pointer"
                    onClick={() => handleUserSelection(user.id)}
                  >
                    <div className="flex items-center">
                      <UserAvatar username={user.username} size="sm" className="mr-3" />
                      <div>
                        <p className="font-medium dark:text-white light:text-gray-800">{user.username}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {user.is_staff && (
                        <Badge className="bg-purple-500 text-white">Admin</Badge>
                      )}
                      {user.is_adjudicator && (
                        <Badge className="bg-green-500 text-white">Adjudicator</Badge>
                      )}
                      <div className={`h-5 w-5 rounded-full border flex items-center justify-center
                        ${selectedUserIds.includes(user.id) ? 'bg-blue-500 border-blue-500' : 'border-gray-400'}`}
                      >
                        {selectedUserIds.includes(user.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-400">
                  No users match your search
                </div>
              )}
            </div>
            
            <div className="mt-4 text-sm text-gray-400">
              {selectedUserIds.length} user(s) selected
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUserSelectionDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowUserSelectionDialog(false)}
            >
              Confirm Selection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default NotificationManagement;